import React, {
  memo,
  useRef,
  useMemo,
  useState,
  useCallback,
  useEffect,
} from "react";
import { Container } from "@pixi/react";
import { Settings } from "../../../settings";
import Reel from "../Reel/Reel";
import { useReelRefs } from "../../../Contexts/ReelRefsContext";
import ReelSetMask from "../ReelSetMask/ReelSetMask";
import * as PIXI from "pixi.js";
import { ASSET_NAME } from "../../../assets";
import { useSelector } from "react-redux";
import {
  selectInitialReels,
  selectHoldAndWinActive,
} from "../../../State/selectors";
import { useReelLandingAnimations } from "../../../Hooks/useLandingAnimations";

// Module-level cache for initial reels - set once, never changes
// This prevents Redux re-renders from affecting ReelSet
let cachedInitialReels = null;

const ReelSet = memo(
  ({ symbolWidth, symbolHeight, dividerDimensions, reelSpacing }) => {
    const { reelRefs } = useReelRefs();
    const { triggerReelLandingAnimations } = useReelLandingAnimations();
    const initialReelsFromRedux = useSelector(selectInitialReels);
    const maskRef = useRef(null);
    const [maskReady, setMaskReady] = useState(false);
    const holdAndWinActive = useSelector(selectHoldAndWinActive);

    // Cache initial reels at module level - set once and freeze the reference
    // This prevents re-renders when Redux state updates
    if (!cachedInitialReels && initialReelsFromRedux) {
      cachedInitialReels = initialReelsFromRedux;
    }

    // Use cached reels (wait for Redux state, no fallback needed)
    const initialReels = cachedInitialReels;

    // Callback ref to track when mask is ready
    const setMaskRef = useCallback(
      (node) => {
        maskRef.current = node;
        if (node && !maskReady) {
          setMaskReady(true);
        }
      },
      [maskReady]
    );

    const triggerLandingRef = useRef(triggerReelLandingAnimations);
    useEffect(() => {
      triggerLandingRef.current = triggerReelLandingAnimations;
    }, [triggerReelLandingAnimations]);

    const onReelStop = useCallback((reelIndex, reelData) => {
      if (!reelData) return;
      try {
        triggerLandingRef.current?.(reelIndex, reelData);
      } catch (err) {
        console.error(`[Reel ${reelIndex}] onReelStop error:`, err);
      }
    }, []);

    // ✅ Memoized selector (no unnecessary re-renders)

    // Load assets once
    const symbolsSpriteSheet = useMemo(
      () => PIXI.Assets.get(ASSET_NAME.reel_symbols),
      []
    );
    const symbolsSpriteSheetBlurred = useMemo(
      () => PIXI.Assets.get(ASSET_NAME.reel_symbols_blurred),
      []
    );

    // Cache symbols data
    const reelSymbolsData = useMemo(() => {
      if (!symbolsSpriteSheet) return null;
      return (
        symbolsSpriteSheet.data?.frames || symbolsSpriteSheet.textures || null
      );
    }, [symbolsSpriteSheet]);

    // Guard: Don't render until initialReels and sprite textures are ready
    if (
      !initialReels ||
      !Array.isArray(initialReels) ||
      initialReels.length === 0 ||
      !symbolsSpriteSheet?.textures ||
      !symbolsSpriteSheetBlurred?.textures
    ) {
      return null;
    }

    return (
      <>
        <ReelSetMask
          ref={setMaskRef}
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
          dividerDimensions={dividerDimensions}
        />

        {maskReady && (
          <Container
            visible={!holdAndWinActive}
            name="reelset"
            mask={maskRef.current}
            x={0}
            y={0}
            zIndex={1}
          >
            {initialReels.map((reel, index) => (
              <Container
                key={index}
                name={`reel_container_${index}`}
                x={symbolWidth * (index - 2)}
                y={
                  -symbolHeight * (1.5 * Settings.SYMBOLS_VISIBLE - 0.5) -
                  symbolHeight
                }
              >
                <Reel
                  ref={(el) => (reelRefs.current[index] = el)}
                  reel={reel}
                  index={index}
                  symbolWidth={symbolWidth}
                  symbolHeight={symbolHeight}
                  symbolsSpriteSheet={symbolsSpriteSheet}
                  symbolsSpriteSheetBlurred={symbolsSpriteSheetBlurred}
                  reelSymbolsData={reelSymbolsData}
                  onReelStop={onReelStop}
                />
              </Container>
            ))}
          </Container>
        )}
      </>
    );
  }
);

export default ReelSet;
